import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectProps,
} from "@mui/material";
import { useField } from "formik";

import { useGetUtilityCurrencies } from "hooks/useGetUtilityCurrencies";
import { CURRENCY_DEFAULT } from "../../../constants/constants";

type CurrencySelectProps = SelectProps & {
  name: string;
};

export const CurrencySelect = ({
  name,
  ...props
}: CurrencySelectProps): JSX.Element => {
  const [{ value, onBlur }, { error, touched }, { setValue }] = useField(name);
  const { t } = useTranslation();
  const { currencies, isLoading } = useGetUtilityCurrencies();
  const hasError = useMemo(() => touched && Boolean(error), [touched, error]);
  const onChange = useCallback(
    ({ target }) => {
      setValue(target.value);
    },
    [setValue]
  );

  return (
    <FormControl error={hasError} size={props.size} fullWidth={props.fullWidth}>
      <InputLabel>{props.label}</InputLabel>
      <Select
        {...props}
        label={props.label}
        value={value ?? CURRENCY_DEFAULT}
        onBlur={onBlur}
        displayEmpty
        disabled={props.disabled || isLoading}
        onChange={onChange}
        renderValue={
          isLoading
            ? () => (
                <>
                  <CircularProgress size={16} sx={{ marginRight: 2 }} />
                  {t("Loading")}
                </>
              )
            : undefined
        }
      >
        {currencies.map((currency) => (
          <MenuItem key={currency.value} value={currency.value}>
            {currency.name}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText error>{hasError ? error : " "}</FormHelperText>
    </FormControl>
  );
};
